/**
 * A minimal History API router.
 *
 * EFSS has five flat routes, no nested layouts, no data loaders and no SSR. Every
 * published version of react-router in range carries security advisories, almost all of
 * them in SSR and RSC code paths this app will never execute — but carrying that
 * dependency, and its advisory feed, to serve five links is a bad trade for something
 * meant to run for years. See DECISIONS.md D41.
 *
 * Handles the two things people actually notice: the back button, and deep links.
 *
 * Components only. The plain functions live in `navigation.ts` so React Fast Refresh keeps
 * working — a module exporting both cannot be hot-swapped.
 */

import {
  useCallback,
  useEffect,
  useState,
  type AnchorHTMLAttributes,
  type MouseEvent,
  type ReactNode,
} from "react";

import { NAVIGATION_EVENT, isModifiedClick, navigate } from "./navigation";
import { PathContext, usePath } from "./pathContext";

export function RouterProvider({ children }: { children: ReactNode }) {
  const [path, setPath] = useState(() => window.location.pathname);

  useEffect(() => {
    const sync = () => setPath(window.location.pathname);
    window.addEventListener("popstate", sync);
    window.addEventListener(NAVIGATION_EVENT, sync);
    return () => {
      window.removeEventListener("popstate", sync);
      window.removeEventListener(NAVIGATION_EVENT, sync);
    };
  }, []);

  return <PathContext.Provider value={path}>{children}</PathContext.Provider>;
}

export function Link({
  to,
  children,
  className,
  ...rest
}: {
  to: string;
  children: ReactNode;
  className?: string;
} & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href">) {
  const path = usePath();
  const active = path === to;

  const onClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      // Leave modified clicks alone so "open in new tab" and middle-click still work.
      if (isModifiedClick(event)) return;
      event.preventDefault();
      navigate(to);
    },
    [to],
  );

  return (
    <a
      href={to}
      onClick={onClick}
      className={className}
      aria-current={active ? "page" : undefined}
      {...rest}
    >
      {children}
    </a>
  );
}
